import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { loadLeaflet } from "@/lib/leaflet";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Crosshair } from "lucide-react";
import { toast } from "sonner";

const BJB = [-3.4425, 114.8306];
const pinIcon = (L, color) => L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 0 1px #0006"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

const distM = (a, b) => {
  const R = 6371000, r = x => x * Math.PI / 180;
  const dLat = r(b[0] - a[0]), dLng = r(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a[0])) * Math.cos(r(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const SherlockManualDialog = ({ open, onOpenChange, woId, onSaved }) => {
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const pickRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !woId) return;
    setCtx(null); setPick(null);
    api.sherlockContext(woId).then(setCtx).catch(() => toast.error("Gagal memuat posisi armada"));
  }, [open, woId]);

  useEffect(() => {
    if (!ctx || !boxRef.current) return;
    let cancelled = false;
    loadLeaflet().then(L => {
      if (cancelled || !boxRef.current) return;
      const woPt = ctx.wo.lat != null ? [ctx.wo.lat, ctx.wo.lng] : null;
      const arPt = ctx.armada ? [ctx.armada.lat, ctx.armada.lng] : null;
      const map = L.map(boxRef.current).setView(arPt || woPt || BJB, 17);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 20 }).addTo(map);
      if (ctx.trail.length >= 2) L.polyline(ctx.trail, { color: "#EF4444", weight: 3, opacity: 0.7 }).addTo(map);
      if (woPt) L.marker(woPt, { icon: pinIcon(L, "#71717A") }).addTo(map).bindTooltip("Sherlock lama");
      if (arPt) L.marker(arPt, { icon: L.icon({ iconUrl: "/armada-marker.png", iconSize: [56, 37], iconAnchor: [28, 18] }) })
        .addTo(map).bindTooltip(`${ctx.armada.name || "Armada"} · ${ctx.armada.age_min ?? "?"} mnt lalu`);
      const start = arPt || woPt || BJB;
      const pm = L.marker(start, { icon: pinIcon(L, "#10B981"), draggable: true, zIndexOffset: 1000 }).addTo(map)
        .bindTooltip("Sherlock baru (geser / klik peta)", { permanent: false });
      pm.on("dragend", () => { const p = pm.getLatLng(); setPick([p.lat, p.lng]); });
      map.on("click", e => { pm.setLatLng(e.latlng); setPick([e.latlng.lat, e.latlng.lng]); });
      setPick(start);
      pickRef.current = pm;
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 200);
    });
    return () => { cancelled = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [ctx]);

  const useArmada = () => {
    if (!ctx?.armada) return;
    const p = [ctx.armada.lat, ctx.armada.lng];
    pickRef.current?.setLatLng(p); mapRef.current?.setView(p); setPick(p);
  };

  const save = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const r = await api.sherlockManual(woId, { lat: pick[0], lng: pick[1] });
      toast.success(`Sherlock dikoreksi${r.distance_m != null ? ` (geser ${Math.round(r.distance_m)} m)` : ""}${r.other_wos ? ` · ${r.other_wos} WO lain ikut diperbarui` : ""}`);
      onSaved?.(r.wo);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan koreksi");
    } finally { setBusy(false); }
  };

  const oldPt = ctx?.wo?.lat != null ? [ctx.wo.lat, ctx.wo.lng] : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="sherlock-manual-dialog">
        <DialogHeader><DialogTitle>Koreksi Sherlock Manual</DialogTitle></DialogHeader>
        <div className="text-[11px] text-zinc-500 -mt-2">
          Geser pin hijau atau klik peta di titik lokasi customer yang benar. Abu-abu = sherlock lama, merah = jejak armada 2 jam terakhir.
        </div>
        {!ctx ? (
          <div className="h-[380px] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
        ) : (
          <div ref={boxRef} className="h-[380px] w-full rounded-sm border border-border" data-testid="sherlock-manual-map" />
        )}
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          {ctx && !ctx.armada && <span className="text-amber-600" data-testid="sherlock-manual-no-gps">Posisi GPS armada tidak tersedia</span>}
          {pick && <span className="font-mono text-zinc-600" data-testid="sherlock-manual-pick">{pick[0].toFixed(6)}, {pick[1].toFixed(6)}</span>}
          {pick && oldPt && <span className="text-zinc-500">· {Math.round(distM(oldPt, pick))} m dari sherlock lama</span>}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={useArmada} disabled={!ctx?.armada} data-testid="sherlock-manual-use-armada">
              <Crosshair className="w-3.5 h-3.5 mr-1" />Posisi armada
            </Button>
            <Button size="sm" onClick={save} disabled={!pick || busy} data-testid="sherlock-manual-save">
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Simpan Sherlock
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
